import { useState, createContext, useContext, useCallback, useEffect } from "react";
import { getAndroidProducts } from "@/lib/android/getAndroidProducts";
import { verifyAndroidPurchase } from "@/lib/android/verifyAndroidPurchase";
import { detectUserRegion } from "@/utilities/detectUserRegion";
import { useIsAndroid } from "@/hooks/useIsAndroid";
import type {
  AndroidProductEnhanced,
  AndroidProductsRawResponse,
  GooglePlaySubscription,
  GooglePlayPrice,
  AndroidPaymentStatus,
  AndroidSubscriptionPlan,
} from "@/lib/types";
import { useConfetti } from "./confettiProvider";
import { useUser } from "@/hooks/useUser";
import { SUBSCRIPTION_SKUS } from "@/config/constants";

type PlayBillingContextType = {
  paymentStatus: AndroidPaymentStatus;
  purchaseSubscription: (plan: AndroidSubscriptionPlan) => Promise<boolean>;
  products?: { monthly: AndroidProductEnhanced; yearly: AndroidProductEnhanced };
};

const PlayBillingContext = createContext<PlayBillingContextType | null>(null);

// Helper functions
const formatGooglePlayPrice = (
  units: string,
  nanos: number | undefined,
  currencyCode: string,
): string => {
  const totalPrice = Number.parseFloat(units) + (nanos || 0) / 1000000000;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalPrice);
  } catch {
    return `${currencyCode} ${totalPrice.toFixed(2)}`;
  }
};

const getProductPricingForRegion = (
  product: GooglePlaySubscription,
  regionCode: string,
): GooglePlayPrice | null => {
  if (!product?.basePlans?.[0]?.regionalConfigs) {
    return null;
  }

  const regionalConfigs = product.basePlans[0].regionalConfigs;
  let priceConfig = regionalConfigs.find((config) => config.regionCode === regionCode);

  if (!priceConfig) {
    priceConfig = regionalConfigs[0];
  }

  return priceConfig?.price || null;
};

const getProductTitleFromRaw = (product: GooglePlaySubscription, fallbackTitle: string): string => {
  const englishListing = product?.listings?.find(
    (listing) => listing.languageCode === "en-US" || listing.languageCode === "en",
  );

  return englishListing?.title || product?.listings?.[0]?.title || fallbackTitle;
};

const createPaymentRequest = (sku: string, product: AndroidProductEnhanced) => {
  const methodData = [
    {
      supportedMethods: "https://play.google.com/billing",
      data: {
        sku,
        type: "subscription",
      },
    },
  ];

  const details = {
    total: {
      label: product.title,
      amount: {
        currency: product.price.currency,
        value: "0.00", // This will be overridden by the actual product price from Play Store
      },
    },
  };

  return new PaymentRequest(methodData, details);
};

const processProductData = (
  rawData: AndroidProductsRawResponse,
  regionCode: string,
): { monthly: AndroidProductEnhanced; yearly: AndroidProductEnhanced } => {
  const monthlyProduct = rawData.monthly;
  const yearlyProduct = rawData.yearly;

  const monthlyPrice = monthlyProduct
    ? getProductPricingForRegion(monthlyProduct, regionCode)
    : null;
  const yearlyPrice = yearlyProduct ? getProductPricingForRegion(yearlyProduct, regionCode) : null;

  return {
    monthly: {
      itemId: SUBSCRIPTION_SKUS.monthly,
      title: monthlyProduct
        ? getProductTitleFromRaw(monthlyProduct, "Monthly Subscription")
        : "Monthly Subscription",
      description: "Monthly access to ProgressMade Pro features",
      price: {
        currency: monthlyPrice?.currencyCode || "USD",
        value: monthlyPrice
          ? (
              Number.parseFloat(monthlyPrice.units) +
              (monthlyPrice.nanos || 0) / 1000000000
            ).toFixed(2)
          : "9.99",
        formattedPrice: monthlyPrice
          ? formatGooglePlayPrice(monthlyPrice.units, monthlyPrice.nanos, monthlyPrice.currencyCode)
          : undefined,
      },
    },
    yearly: {
      itemId: SUBSCRIPTION_SKUS.yearly,
      title: yearlyProduct
        ? getProductTitleFromRaw(yearlyProduct, "Yearly Subscription")
        : "Yearly Subscription",
      description: "Yearly access to ProgressMade Pro features with 40% savings",
      price: {
        currency: yearlyPrice?.currencyCode || "USD",
        value: yearlyPrice
          ? (Number.parseFloat(yearlyPrice.units) + (yearlyPrice.nanos || 0) / 1000000000).toFixed(
              2,
            )
          : "59.99",
        formattedPrice: yearlyPrice
          ? formatGooglePlayPrice(yearlyPrice.units, yearlyPrice.nanos, yearlyPrice.currencyCode)
          : undefined,
      },
    },
  };
};

export function PlayBillingProvider({ children }: { children: React.ReactNode }) {
  const isAndroid = useIsAndroid();
  const { userId } = useUser();
  const { fireConfetti } = useConfetti();
  const [paymentStatus, setPaymentStatus] = useState<AndroidPaymentStatus>("idle");
  const [products, setProducts] = useState<{
    monthly: AndroidProductEnhanced;
    yearly: AndroidProductEnhanced;
  }>();

  const regionCode = detectUserRegion();

  const purchaseSubscription = useCallback(
    async (plan: AndroidSubscriptionPlan): Promise<boolean> => {
      if (!window.PaymentRequest) {
        return false;
      }

      setPaymentStatus("loading");

      try {
        const sku = plan === "monthly" ? SUBSCRIPTION_SKUS.monthly : SUBSCRIPTION_SKUS.yearly;
        const product = plan === "monthly" ? products?.monthly : products?.yearly;

        if (!product) {
          throw new Error("Product not found");
        }

        const paymentRequest = createPaymentRequest(sku, product);

        const canMakePayment = await paymentRequest.canMakePayment();

        if (!canMakePayment) {
          throw new Error("Google Play Billing not available for this SKU");
        }

        const paymentResponse = await paymentRequest.show();
        const purchaseToken = paymentResponse.details?.purchaseToken;

        if (purchaseToken) {
          await paymentResponse.complete("success");

          setPaymentStatus("success");

          fireConfetti({ golden: true });

          // Verify and acknowledge the purchase on the backend
          // https://developer.android.com/google/play/billing/security#verify
          // https://developer.chrome.com/docs/android/trusted-web-activity/receive-payments-play-billing#retrieve_details_for_a_sku
          try {
            const verificationResult = await verifyAndroidPurchase({
              data: {
                userId: userId || "",
                selectedPlan: plan,
                purchaseToken,
                productId: sku,
              },
            });

            if (!verificationResult.success || !verificationResult.isValid) {
              console.warn("Purchase verification failed:", verificationResult.error);
              // Note: We still return true here to avoid disrupting user experience
              // You may want to implement additional handling based on your business logic
            }
          } catch (error) {
            console.error("Purchase verification error:", error);
            // Note: We still return true here to avoid disrupting user experience
          }

          return true;
        }

        throw new Error("No purchase token received");
      } catch (error) {
        setPaymentStatus("error");
        return false;
      }
    },
    [products, regionCode, fireConfetti],
  );

  const fetchProducts = useCallback(async () => {
    try {
      const rawData: AndroidProductsRawResponse = await getAndroidProducts({
        data: {
          regionCode,
        },
      });

      const processedProducts = processProductData(rawData, regionCode);
      setProducts(processedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [regionCode]);

  // Fetch Android product prices on Android devices
  useEffect(() => {
    if (isAndroid) {
      fetchProducts();
    }
  }, [isAndroid, fetchProducts]);

  return (
    <PlayBillingContext.Provider
      value={{
        paymentStatus,
        purchaseSubscription,
        products,
      }}
    >
      {children}
    </PlayBillingContext.Provider>
  );
}

export const usePlayBilling = () => {
  const context = useContext(PlayBillingContext);

  if (!context) {
    throw new Error("usePlayBilling must be used within an PlayBillingProvider");
  }

  return context;
};
